import {
  Activity,
  AlertTriangle,
  Ban,
  CalendarRange,
  CheckCircle2,
  CircleDollarSign,
  Download,
  Gift,
  RefreshCw,
  RotateCcw,
  TicketCheck,
  TrendingUp,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode, type SyntheticEvent } from "react";
import {
  type TenantRewardAnalyticsBreakdown,
  type TenantRewardAnalyticsMetric,
  type TenantRewardAnalyticsReport,
  type TenantRewardTierAnalyticsBreakdown,
  fetchBusinessSubscription,
  fetchTenantRewardAnalytics,
  messageFromError,
} from "../api";
import { AppAlert } from "../components/AppAlertDialog";
import {
  SubscriptionUpgradeDialog,
  type SubscriptionUpgradeReason,
} from "../components/SubscriptionUpgradeDialog";
import { featureUpgradeReason, subscriptionIncludes } from "../subscription-access";
import "./TenantRewardAnalytics.css";

export const tenantRewardAnalyticsKey = ["tenant-reward-analytics"] as const;

export interface TenantRewardAnalyticsDateRange {
  fromDate: string;
  toDate: string;
}

const dateInputValue = (date: Date) => date.toISOString().slice(0, 10);

export const defaultTenantRewardAnalyticsDateRange = (now = new Date()): TenantRewardAnalyticsDateRange => {
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 29);
  return { fromDate: dateInputValue(from), toDate: dateInputValue(to) };
};

export const tenantRewardAnalyticsRequestWindow = (range: TenantRewardAnalyticsDateRange) => {
  const from = new Date(`${range.fromDate}T00:00:00.000Z`);
  const inclusiveTo = new Date(`${range.toDate}T00:00:00.000Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(inclusiveTo.getTime()) || from > inclusiveTo) return null;
  const exclusiveTo = new Date(inclusiveTo);
  exclusiveTo.setUTCDate(exclusiveTo.getUTCDate() + 1);
  return { from: from.toISOString(), to: exclusiveTo.toISOString() };
};

const number = (value: number) => new Intl.NumberFormat("en-US").format(value);
const percent = (value: number) => `${(value * 100).toFixed(1)}%`;
const money = (minor: number, currencyCode: string) => new Intl.NumberFormat("en", {
  style: "currency",
  currency: currencyCode,
  maximumFractionDigits: 2,
}).format(minor / 100);
const dateTime = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not available" : new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const csvCell = (value: string | number) => `"${String(value).replaceAll('"', '""')}"`;

export function tenantRewardAnalyticsCsv(report: TenantRewardAnalyticsReport): string {
  const header = [
    "Dimension",
    "ID / code",
    "Name",
    "Opportunities",
    "Issued",
    "Claimed",
    "Redeemed",
    "Expired",
    "Cancelled",
    "Reversed",
    "Redemption rate",
    "Reward cost minor",
    "Associated revenue minor",
  ];
  const metricCells = (metric: TenantRewardAnalyticsMetric) => [
    metric.opportunityCount,
    metric.issuedCount,
    metric.claimedCount,
    metric.redeemedCount,
    metric.expiredCount,
    metric.cancelledCount,
    metric.reversedCount,
    metric.redemptionRate,
    metric.rewardCostMinor,
    metric.associatedRevenueMinor,
  ];
  const rows: Array<Array<string | number>> = [
    ["Summary", "", "All rewards", ...metricCells(report.summary)],
  ];
  const append = (dimension: string, items: TenantRewardAnalyticsBreakdown[]) => {
    rows.push(...items.map((item) => [dimension, item.publicId, item.name, ...metricCells(item)]));
  };
  append("Reward", report.byReward);
  append("Campaign", report.byCampaign);
  append("Location", report.byLocation);
  append("Segment", report.bySegment);
  rows.push(...report.byMeroTier.map((item) => ["Mero tier", item.code, item.name, ...metricCells(item)]));
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
}

function downloadAnalyticsCsv(report: TenantRewardAnalyticsReport) {
  const blob = new Blob([tenantRewardAnalyticsCsv(report)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `mero-perks-reward-analytics-${report.window.from.slice(0, 10)}-${new Date(new Date(report.window.to).getTime() - 1).toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function MetricCard({ icon, label, value, detail }: { icon: ReactNode; label: string; value: string; detail: string }) {
  return <article className="reward-analytics-kpi"><span aria-hidden="true">{icon}</span><div><small>{label}</small><strong>{value}</strong><p>{detail}</p></div></article>;
}

type DisplayRow = TenantRewardAnalyticsBreakdown | TenantRewardTierAnalyticsBreakdown;

function DimensionTable({
  title,
  description,
  rows,
  currencyCode,
}: {
  title: string;
  description: string;
  rows: DisplayRow[];
  currencyCode: string;
}) {
  return <section className="reward-analytics-dimension">
    <div className="reward-analytics-dimension__heading"><div><h3>{title}</h3><p>{description}</p></div><span>{number(rows.length)} rows</span></div>
    <div className="reward-analytics-table-scroll" tabIndex={0} role="region" aria-label={`${title} analytics table. Scroll horizontally to see every metric.`}>
      <table>
        <caption>{title} reward performance for the selected reporting period</caption>
        <thead><tr><th scope="col">Name</th><th scope="col">Issued</th><th scope="col">Claimed</th><th scope="col">Redeemed</th><th scope="col">Expired</th><th scope="col">Cancelled</th><th scope="col">Reversed</th><th scope="col">Rate</th><th scope="col">Cost</th><th scope="col">Revenue</th></tr></thead>
        <tbody>{rows.length ? rows.map((row) => {
          const identifier = "publicId" in row ? row.publicId : row.code;
          return <tr key={identifier}><th scope="row"><strong>{row.name}</strong><small>{identifier}</small></th><td>{number(row.issuedCount)}</td><td>{number(row.claimedCount)}</td><td>{number(row.redeemedCount)}</td><td>{number(row.expiredCount)}</td><td>{number(row.cancelledCount)}</td><td>{number(row.reversedCount)}</td><td>{percent(row.redemptionRate)}</td><td>{money(row.rewardCostMinor, currencyCode)}</td><td>{money(row.associatedRevenueMinor, currencyCode)}</td></tr>;
        }) : <tr><td className="reward-analytics-table-empty" colSpan={10}>No activity was recorded for this dimension in the selected period.</td></tr>}</tbody>
      </table>
    </div>
  </section>;
}

export function TenantRewardAnalytics({
  canView,
  currencyCode = "NPR",
  initialRange,
}: {
  canView: boolean;
  currencyCode?: string;
  initialRange?: TenantRewardAnalyticsDateRange;
}) {
  const startingRange = useMemo(() => initialRange ?? defaultTenantRewardAnalyticsDateRange(), [initialRange]);
  const [draftRange, setDraftRange] = useState(startingRange);
  const [appliedRange, setAppliedRange] = useState(startingRange);
  const [rangeError, setRangeError] = useState("");
  const [upgradeReason, setUpgradeReason] = useState<SubscriptionUpgradeReason | null>(null);
  const subscription = useQuery({ queryKey: ["tenant-subscription"], queryFn: fetchBusinessSubscription });
  const requestWindow = tenantRewardAnalyticsRequestWindow(appliedRange);
  const analyticsQuery = useQuery({
    queryKey: [...tenantRewardAnalyticsKey, requestWindow?.from, requestWindow?.to],
    queryFn: () => {
      if (!requestWindow) throw new Error("Choose a valid reporting period.");
      return fetchTenantRewardAnalytics(requestWindow);
    },
    enabled: canView && requestWindow !== null,
  });

  const applyRange = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tenantRewardAnalyticsRequestWindow(draftRange)) {
      setRangeError("The start date must be on or before the end date.");
      return;
    }
    setRangeError("");
    setAppliedRange(draftRange);
  };

  if (!canView) {
    return <section className="portal-panel reward-analytics-state" role="status"><AlertTriangle /><h2>Analytics access is not assigned</h2><p>Ask a Business Owner administrator for the reward.analytics permission.</p></section>;
  }

  const report = analyticsQuery.data;
  return <section className="reward-analytics" aria-labelledby="reward-analytics-title">
    {upgradeReason && <SubscriptionUpgradeDialog reason={upgradeReason} close={() => { setUpgradeReason(null); }} />}
    <header className="portal-panel reward-analytics-header">
      <div><p className="eyebrow">Measured reward outcomes</p><h2 id="reward-analytics-title">Reward analytics</h2><p>Compare issuance, redemption, cost, and associated verified revenue without mixing Business Owner rewards with Mero Tier Points.</p></div>
      <div className="reward-analytics-header__actions">
        <button className="button button--outline button--compact" disabled={!report} type="button" onClick={() => {
          if (!report) return;
          if (subscription.data && !subscriptionIncludes(subscription.data, "data_exports")) {
            setUpgradeReason(featureUpgradeReason(subscription.data, { feature: "data_exports", label: "Data exports and full reporting" }));
            return;
          }
          downloadAnalyticsCsv(report);
        }}><Download /> Export CSV</button>
        <button className="button button--quiet button--compact" disabled={analyticsQuery.isFetching} type="button" onClick={() => { void analyticsQuery.refetch(); }}><RefreshCw /> Refresh</button>
      </div>
      <form className="reward-analytics-range" onSubmit={applyRange}>
        <CalendarRange aria-hidden="true" />
        <label><span>From</span><input required type="date" value={draftRange.fromDate} onChange={(event) => { setDraftRange((current) => ({ ...current, fromDate: event.target.value })); }} /></label>
        <label><span>Through</span><input required type="date" value={draftRange.toDate} onChange={(event) => { setDraftRange((current) => ({ ...current, toDate: event.target.value })); }} /></label>
        <button className="button button--primary button--compact" type="submit">Apply dates</button>
        {rangeError && <AppAlert message={rangeError} onDismiss={() => { setRangeError(""); }} />}
      </form>
      {report?.generatedAt && <small className="reward-analytics-generated">Generated {dateTime(report.generatedAt)} · End date is inclusive</small>}
    </header>

    {analyticsQuery.isLoading ? <div className="portal-panel reward-analytics-state" role="status"><Activity className="spinner" /><h3>Loading reward analytics</h3></div>
      : analyticsQuery.isError ? <><AppAlert message={messageFromError(analyticsQuery.error)} /><div className="portal-panel reward-analytics-state reward-analytics-state--error"><AlertTriangle /><h3>Analytics could not be loaded</h3><p>Try loading this report again.</p><button className="button button--outline" type="button" onClick={() => { void analyticsQuery.refetch(); }}>Try again</button></div></>
        : report ? <>
          <div className="reward-analytics-kpis" aria-label="Reward analytics key performance indicators">
            <MetricCard icon={<Gift />} label="Issued" value={number(report.summary.issuedCount)} detail="Reward issuances created" />
            <MetricCard icon={<TicketCheck />} label="Claimed" value={number(report.summary.claimedCount)} detail="Redemption claims opened" />
            <MetricCard icon={<CheckCircle2 />} label="Redeemed" value={number(report.summary.redeemedCount)} detail="Claims fulfilled" />
            <MetricCard icon={<Activity />} label="Expired" value={number(report.summary.expiredCount)} detail="Opportunities expired" />
            <MetricCard icon={<Ban />} label="Cancelled" value={number(report.summary.cancelledCount)} detail="Opportunities cancelled" />
            <MetricCard icon={<RotateCcw />} label="Reversed" value={number(report.summary.reversedCount)} detail="Fulfillments reversed" />
            <MetricCard icon={<TrendingUp />} label="Redemption rate" value={percent(report.summary.redemptionRate)} detail={`${number(report.summary.redeemedCount)} of ${number(report.summary.opportunityCount)} opportunities`} />
            <MetricCard icon={<CircleDollarSign />} label="Reward cost" value={money(report.summary.rewardCostMinor, currencyCode)} detail="Fulfilled value net of reversals" />
            <MetricCard icon={<CircleDollarSign />} label="Associated revenue" value={money(report.summary.associatedRevenueMinor, currencyCode)} detail="Verified purchase value at fulfillment" />
          </div>

          <div className="reward-analytics-dimensions">
            <DimensionTable title="By reward" description="Compare each offer's demand, fulfillment, and economics." rows={report.byReward} currencyCode={currencyCode} />
            <DimensionTable title="By campaign" description="Measure governed campaign issuance and conversion." rows={report.byCampaign} currencyCode={currencyCode} />
            <DimensionTable title="By location" description="See where claims and fulfilled value were recorded." rows={report.byLocation} currencyCode={currencyCode} />
            <DimensionTable title="By segment" description="Compare campaign outcomes across saved audiences." rows={report.bySegment} currencyCode={currencyCode} />
            <DimensionTable title="By Mero tier" description="Attribute fulfillment to the member's effective platform tier." rows={report.byMeroTier} currencyCode={currencyCode} />
          </div>

          <details className="portal-panel reward-analytics-definitions">
            <summary>Metric definitions</summary>
            <dl><div><dt>Issued</dt><dd>{report.definitions.issued}</dd></div><div><dt>Claimed</dt><dd>{report.definitions.claimed}</dd></div><div><dt>Redeemed</dt><dd>{report.definitions.redeemed}</dd></div><div><dt>Reward cost</dt><dd>{report.definitions.rewardCost}</dd></div><div><dt>Associated revenue</dt><dd>{report.definitions.associatedRevenue}</dd></div><div><dt>Tier attribution</dt><dd>{report.definitions.tierAttribution}</dd></div></dl>
          </details>
        </> : null}
  </section>;
}
